/** The four steps, in order. Mirrors SIGNUP_STEPS. */
export const REGISTER_STEPS = [
  "Upload Images",
  "Artwork Details",
  "Provenance",
  "Confirm",
] as const;

/**
 * The declarations a collector signs, and the views a work is expected to
 * carry — re-exported from `@ondo/shared`.
 *
 * THEY MOVED, and the move is the point. This is legal text attached to an act
 * that creates a permanent registry record, and the mobile app has to show the
 * same four sentences. Of everything in this product, the text somebody signs
 * is the worst thing to keep two slightly different copies of: a collector
 * ticking four boxes on a phone must be agreeing to exactly what a collector
 * ticking four boxes here agreed to.
 *
 * Still PLACEHOLDER WORDING, and still needing sign-off before it ships — see
 * the docblock where it now lives. Re-exported rather than imported at each
 * call site so nothing in this screen had to change.
 */
export { DECLARATIONS } from "@ondo/shared";
export type { DeclarationKey } from "@ondo/shared";

/**
 * What the file picker accepts.
 *
 * The same five types `ondo.work_images.content_type` allows, and that is not a
 * coincidence to be tidied away: a file this list lets through and the check
 * constraint refuses would fail at the end of the wizard, after the collector
 * had already filled in three more steps.
 */
export const ACCEPTED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/tiff",
  "image/heic",
  "image/webp",
] as const;

/** 50MB, as the dropzone says. */
export const MAX_IMAGE_BYTES = 50 * 1024 * 1024;

/** Front, back, signature and a full set of details, with room to spare. */
export const MAX_IMAGES = 24;

/**
 * The two views every submission is expected to carry.
 *
 * Expected, not enforced. `validateStep` still lets the images step pass empty —
 * an authority asking for a photograph is a conversation, not a form error — so
 * these drive what the panel says is outstanding and nothing else.
 */
export const CORE_VIEWS = ["front", "back"] as const;

/** The views a collector can add to that list for this particular work. */
export const ADDITIONAL_VIEWS = [
  "stamp",
  "gallery_label",
  "edition_number",
  "certificate",
  "detail",
] as const;

export const REGISTER_FAQ = [
  {
    key: "what",
    question: "What does registering a work mean?",
    answer:
      "It creates a record of the work in your collection, immediately. Anyone may register anything — the registry does not decide what is real, and there is no queue and nobody whose permission you are waiting for.",
  },
  {
    key: "who",
    question: "Then what makes a record worth anything?",
    answer:
      "Its provenance, and who is willing to put their name to it. You record the chain of title — who held the work, when, and how they came by it — and other ONDO accounts can verify a link, dispute it, or comment on it. Those signatures are the registry's only signal, and ONDO neither adds to them nor resolves them into a verdict.",
  },
  {
    key: "when",
    question: "Can I change or remove it afterwards?",
    answer:
      "The catalogue details, yes. A provenance entry, no: it can be withdrawn but never rewritten, because an entry somebody has verified has to keep saying what they verified. You can also take the work out of your collection — which ends the placement and leaves the record, and everybody's verifications on it, intact.",
  },
] as const;

export const REGISTER_COPY = {
  loading: "Opening the register…",
  fraudWarning:
    "Registering false or misleading information about an artwork is a serious offence. Fraudulent registrations may result in permanent account suspension and legal action.",
  /**
   * Says what happens, not what does not.
   *
   * An earlier draft read "nothing is pending and nobody has to approve it",
   * which invites the reader to wonder what pending would have been. A registry
   * that has removed a queue should not keep describing it.
   */
  ondoIdNote:
    "The record is created immediately, in the collection above. What it comes to be worth is who will verify its provenance.",
  imagesNotStored:
    "Images are attached to the work and visible to anyone you share the collection with. They are not published anywhere.",
  captureUnavailable:
    "Photographing on a phone is not available yet. It needs the phone to sign in as you first — a code that could upload on its own would be a credential with nobody behind it.",
  phoneTitle: "Switch to iPhone App",
  phoneBody:
    "Photograph the work on your phone and sync directly to this registration",
  phoneBadge: "Recommended",
  uploadDivider: "or upload from this device",
  dropTitle: "Drag and drop images here",
  dropFormats: "JPEG, PNG, TIFF — up to 50MB each",
  browse: "Browse files",
  signedLabel: "This work is signed (front or verso)",
  viewsTitle: "Additional required views",
  viewsHelp:
    "Stamps, gallery labels, edition numbers, condition details — add any view that should accompany the record. Nobody reviews these, but anyone deciding whether to verify your provenance will look at them.",
  viewsOutstanding: "Still to photograph",
  viewsComplete: "Every view you have listed has an image.",
  imagesEmpty: "No images yet.",
  uploadFailed: "Could not upload",
  noTarget:
    "You need a collection you can add to before you can register a work. Which collection a work goes into is what records who owns it.",
  destinationHelp:
    "Which collection this work goes into. This is the ownership record — the work belongs to whoever owns the collection, not to whoever fills in this form.",
  artistHelp:
    "Search the ONDO catalogue. If the artist is not listed, type the name — the free text carries your claim either way.",
  /**
   * Picking off the catalogue changes nothing about who may see the work, and
   * — since OND-20 — nothing about any catalogue either. What it changes is
   * that the attribution points at a record the registry curates rather than at
   * a string, which is what makes it possible to ask the party appointed over
   * that record to confirm it.
   *
   * Still true as written, and checked rather than assumed: naming an artist
   * who is not listed records the registrant's own claim and nothing else,
   * which is exactly what it said before. The only thing that has changed for
   * this branch is that `hasAuthority` no longer says something STRONGER.
   */
  noAuthority:
    "This artist is not in the ONDO catalogue. The name you typed is recorded as your attribution.",
  /**
   * REWRITTEN BY OND-20, AND THE OLD SENTENCE WAS FALSE.
   *
   * It read "Attributed to this artist in the ONDO catalogue." — which told a
   * registrant that picking a name off the list had put their work into that
   * artist's catalogue raisonné. Since
   * `20261004096000_artist_attribution.sql` it has not, and the migration says
   * why in terms: a catalogue number is minted by a CONFIRMED authorship claim
   * and by nothing else, so "naming an artist mints nothing, and the forged Fat
   * Car is never row 42". The old wording was the registry vouching for a claim
   * nobody had looked at.
   *
   * So this now says the two things that are true: the name is the registrant's
   * own attribution, and confirmation is a separate thing somebody has to ask
   * for explicitly. It does not promise the ask from here — the request is made
   * from the work's own record, on the claim's entry, and a sentence sending
   * somebody to a button this wizard does not have would be the next thing to
   * go stale.
   *
   * AND IT STILL NAMES NO BODY. `describeRouting`'s test asserts this copy
   * never says estate, studio, foundation or an ONDO id, and the omission is a
   * security boundary rather than a style: `ondo.search_artists()` returns no
   * principal, and a sentence naming the party appointed over an artist would
   * make this form a walkable directory of every estate ONDO has signed. "An
   * authorised authority for this artist" is the strongest thing it may say,
   * which is why the first draft of this sentence — "the artist, studio or
   * estate" — was rewritten rather than the guard relaxed.
   */
  hasAuthority:
    "This artist is in the ONDO catalogue, so the name is recorded as a link rather than as free text. It is still your attribution and nothing more: it adds the work to no catalogue and asks nobody to confirm it. Confirmation is a separate, explicit request to an authorised authority for this artist, made from the work’s own record once it exists.",
  provenanceHelp:
    "Add individual ownership records to build a documented provenance chain. Each entry can include supporting evidence.",
  /**
   * The correction the mock needs.
   *
   * Its callout says records can be "added or refined at any time". Added, yes —
   * a provenance entry may be filed at any point, including while an authority
   * is reading the submission. Refined, no: an entry is frozen the moment it is
   * recorded and can afterwards only be withdrawn, because an entry somebody has
   * attested to must still say what they attested to.
   */
  provenanceOptional:
    "Provenance is optional. You can register the work now and add ownership records at any time afterwards — and an incomplete chain is the ordinary state of one, not a failing.",
  exhibitionsHelp:
    "One line per exhibition, as it would read in a catalogue. Still free text — exhibitions have no structured record behind them yet.",
  /**
   * OND-90. The confirmation step, and it is modelled on a bank transfer on
   * purpose: the owner's answer was "a Monzo-style 'did you mean one of these?'
   * step, and if they still say no, tell them the person is not on ONDO
   * currently. So no silent automatic attribution on a near match, and no
   * silent failure either."
   *
   * Both halves of that are copy here, because both halves were silence before:
   * typing a name that nearly matched linked nothing and said nothing, and
   * typing one that matched nothing did the same.
   */
  artistConfirmTitle: "Did you mean one of these?",
  artistConfirmHelp:
    "You typed a name rather than picking an artist from the list. Choosing the right one is what lets you later ask that artist’s studio or estate to confirm the attribution — without it, they have no way of knowing the work exists.",
  artistConfirmNone: "None of these — register the name as I typed it",
  /**
   * WHAT PICKING DOES AND DOES NOT DO, said at the moment of picking.
   *
   * OND-20's rule, which OND-90's own reconciliation section makes binding:
   * "resolving an artist link must not itself notify the studio, request
   * authentication or place an unconfirmed work in its catalogue. Explain that
   * the user must explicitly request attribution confirmation; a matched artist
   * name is not approval." So this sentence has to be read before the choice is
   * made, not after — and it says the same thing `hasAuthority` says, because a
   * registrant who picked from the list and one who picked here are in exactly
   * the same position.
   *
   * NAMES NO BODY, like every other sentence on this screen. The candidate rows
   * carry no principal and this copy may not invent one.
   */
  artistConfirmNote:
    "Linking the artist is still your attribution and nothing more: it adds the work to no catalogue and asks nobody to confirm it. Confirmation is a separate, explicit request to an authorised authority for this artist, made from the work’s own record once it exists.",
  /**
   * THE DECLINE SENTENCE, and the one the ticket says must be plain.
   *
   * It is a statement about the ONDO artist CATALOGUE — a catalogue of names
   * holding no principal — and it is written from a candidate list the
   * registrant has already been shown as empty. It is therefore copy rather
   * than a second question, and it deliberately does not say whether anybody
   * connected with that artist holds an ONDO account: nothing here knows, and
   * an endpoint that did would be the widest directory probe on the platform.
   *
   * It says what it COSTS, which is the part that was missing: the work is
   * still registered, the typed name is still on the record, and the one thing
   * that will not happen is the artist's estate learning of it.
   */
  artistNotOnOndo:
    "No artist by this name is on ONDO at present. The work will be registered with the name exactly as you typed it, and it will not reach that artist’s studio or estate — there is nobody on ONDO for it to reach. You can ask for the attribution to be confirmed later, if they join.",
  /** Shown on Confirm, so the last screen before submitting says which it is. */
  artistLinkedTag: "linked to the ONDO catalogue",
  artistUnlinkedTag: "not in the ONDO catalogue — your attribution only",
  artistMatching: "Checking the artist name…",
  titleRequired: "A work needs a title.",
  artistRequired: "Say who the work is by.",
  targetRequired: "Choose which collection this work goes into.",
  declarationsRequired: "Every declaration has to be accepted.",
  unknownError: "Something went wrong. Please try again.",
  registered: "Registered. It is in your collection now.",
} as const;

